import dateFormat from "dateformat";
import i18next from "i18next";
import {
  UserSubscriptionStatus,
  Inventory,
  UserSubscriptionType,
  EonLinkedAccount,
  EonAccountProvider,
  UserNotification,
  UserNotificationType,
  SubscriptionActivationNotificationData,
  UserSubscription,
} from "@onmo/graphql/graphql";
import { Inventory as OldInventory } from "@onmo/graphql/legacyGraphql";
import { nth, find } from "lodash-es";
import { getDevice } from "./screen";
import { Devices } from "../types/screen";
import { getDayLeft } from "./time";
import { UserSubscriptionPopup } from "../types/user";
import { OTP_LOCKDOWN_STORAGE_KEY } from "../constants/user";

export const showMoreSpaceConsoleCard = (
  status?: UserSubscriptionStatus,
  validUntil?: string,
) => {
  if (!status || !validUntil) return false;
  const expiredCondition = status === UserSubscriptionStatus.Expired;
  const pendingCondition =
    status === UserSubscriptionStatus.Subscribed && getDayLeft(validUntil) < 30;
  const dimensionCondition =
    getDevice() === Devices.Mobile || getDevice() === Devices.Tablet;

  return (expiredCondition || pendingCondition) && dimensionCondition;
};

export const isConsoleActive = (
  status?: UserSubscriptionStatus,
  validUntil?: string,
) => {
  if (!status || !validUntil) return false;
  const activeConditions =
    status === UserSubscriptionStatus.Subscribed &&
    new Date() <= new Date(validUntil);
  const pendingCondition = status === UserSubscriptionStatus.Pending;

  return activeConditions || pendingCondition;
};

export const hasStatus = (status?: UserSubscriptionStatus) => {
  return (
    status === UserSubscriptionStatus.Subscribed ||
    status === UserSubscriptionStatus.Pending ||
    status === UserSubscriptionStatus.Expired ||
    status === UserSubscriptionStatus.Grace
  );
};

export const noLink = (
  status?: UserSubscriptionStatus,
  validUntil?: string,
  isRecurring?: boolean,
) => {
  if (!status || !validUntil) return false;
  if (status !== UserSubscriptionStatus.Subscribed) return false;
  return getDayLeft(validUntil) >= 30 || Boolean(isRecurring);
};

export const getStatusInfo = (
  status?: UserSubscriptionStatus,
  validUntil?: string,
  isRecurring?: boolean,
): "expire-soon" | "subscribed" | "expired" | "not-subscribed" | "grace" => {
  if (!status || !validUntil) return "not-subscribed";
  if (
    status === UserSubscriptionStatus.Subscribed &&
    getDayLeft(validUntil) < 30 &&
    !isRecurring
  )
    return "expire-soon";
  if (
    status === UserSubscriptionStatus.Subscribed &&
    new Date() <= new Date(validUntil)
  )
    return "subscribed";
  if (status === UserSubscriptionStatus.Grace) return "grace";
  return "expired";
};

export const isActivatedSubscription = (
  subscription?: UserSubscription | null,
): boolean => {
  if (!subscription) return false;
  const { status, validUntil, renewalDate } = subscription;
  if (status !== UserSubscriptionStatus.Subscribed) return false;
  if (!validUntil) return false;

  const now = new Date();
  const dates = [validUntil, renewalDate]
    .filter((date) => Boolean(date))
    .map((d) => new Date(d));

  return dates.every((date) => now < date);
};

export const getInventory = (
  avatar?: string,
  inventories?: (Inventory | OldInventory)[],
) => {
  if (!avatar || !inventories) return;
  const inventoryId = nth(avatar.split("/"), -2);
  return find(inventories, { id: inventoryId });
};

export const getSubscriptionStatus = (
  status?: UserSubscriptionStatus,
  isRecurring?: boolean,
  type?: UserSubscriptionType,
  validUntil?: string | null,
): UserSubscriptionPopup => {
  if (
    (type === UserSubscriptionType.Voucher ||
      type === UserSubscriptionType.Onetime) &&
    validUntil &&
    new Date(validUntil) > new Date()
  )
    return "continue";
  if (
    (type === UserSubscriptionType.Autopay && !isRecurring) ||
    status === UserSubscriptionStatus.Expired
  )
    return "resume";
  if (status === UserSubscriptionStatus.NotSubscribed) return "subscribe";
  if (status === UserSubscriptionStatus.Subscribed && isRecurring)
    return "cancel";
  if (status === UserSubscriptionStatus.Grace) return "grace";
  return null;
};

export const getPopupDetail = (
  popupStatus: UserSubscriptionPopup,
  validUntil: string | null,
  renewalDate: string | null,
) => {
  if (popupStatus === "continue")
    return {
      labelBtnKey: "SubscriptionPanel_Continue with Subscription",
      labelTimeKey:
        validUntil && new Date(validUntil) < new Date()
          ? "SubscriptionPanel_Expired on"
          : "SubscriptionPanel_Valid Until",
      time: validUntil && dateFormat(new Date(validUntil), "dd/mm/yyyy"),
    };
  if (popupStatus === "resume")
    return {
      labelBtnKey: "SubscriptionPanel_Resume Subscription",
      labelTimeKey:
        validUntil && new Date(validUntil) < new Date()
          ? "SubscriptionPanel_Expired on"
          : "SubscriptionPanel_Expires on",
      time: validUntil && dateFormat(new Date(validUntil), "dd/mm/yyyy"),
    };
  if (popupStatus === "subscribe")
    return {
      labelBtnKey: "SubscriptionPanel_Subscribe",
    };
  if (popupStatus === "cancel")
    return {
      labelBtnKey: "SubscriptionPanel_Cancel Subscription",
      labelTimeKey: "SubscriptionPanel_Renews on",
      time: renewalDate && dateFormat(new Date(renewalDate), "dd/mm/yyyy"),
    };
  if (popupStatus === "grace")
    return {
      labelBtnKey: "SubscriptionPanel_Cancel Subscription",
      labelTimeKey: "SubscriptionPanel_Payment Failed",
      time: renewalDate && dateFormat(new Date(renewalDate), "dd/mm/yyyy"),
    };
  return {};
};

export const getEonSteamAccount = (eonLinkedAccounts: EonLinkedAccount[]) => {
  return find(eonLinkedAccounts, { provider: EonAccountProvider.Steam });
};

export const isFounderNotifications = (notifications: UserNotification[]) => {
  const subActivated = find(notifications, {
    type: UserNotificationType.SubscriptionActivated,
  });
  const subBonus = find(notifications, {
    type: UserNotificationType.SubscriptionBonusGranted,
  });

  return {
    isFounder: (
      subActivated?.data as SubscriptionActivationNotificationData
    )?.earnedTags?.includes("op_founders"),
    isBonus: !!subBonus,
  };
};

export const getDurationSubscription = (duration: number | undefined) => {
  if (!duration) return { key: "SubscriptionPanel_Month" };
  if (duration >= 365) {
    const year = Math.floor(duration / 365);
    return year > 1
      ? { key: "SubscriptionPanel_Years", value: year }
      : { key: "SubscriptionPanel_Year" };
  } else if (duration >= 30) {
    const month = Math.floor(duration / 30);
    return month > 1
      ? { key: "SubscriptionPanel_Months", value: month }
      : { key: "SubscriptionPanel_Month" };
  } else {
    return duration > 1
      ? { key: "SubscriptionPanel_Days", value: duration }
      : { key: "SubscriptionPanel_Day" };
  }
};

export type StreamDetailField = "resolution" | "fps" | "bitrate";

export const formatStreamValue = (
  field: StreamDetailField,
  value: string,
  enabled = true,
) => {
  if (!value) return i18next.t("StreamingProfileDetails_N/A");
  const formattedValue =
    field === "resolution"
      ? `${value}p`
      : field === "fps"
      ? `${value} FPS`
      : `${Math.round(parseInt(value, 10) / 1000)} Mbps`;

  return enabled
    ? formattedValue
    : i18next.t("StreamingProfileDetails_Coming Soon", {
        value: formattedValue,
      });
};

export const getCurrencySymbol = (currency?: string): string => {
  switch (currency) {
    case "INR":
      return "₹";
    case "USD":
      return "$";
    case "EUR":
      return "€";
    default:
      return currency ?? "";
  }
};

export const isOtpLockdownActive = () => {
  const lockdownUntil = Number(
    window.localStorage.getItem(OTP_LOCKDOWN_STORAGE_KEY),
  );

  if (lockdownUntil > Date.now()) return true;
  if (lockdownUntil) {
    window.localStorage.removeItem(OTP_LOCKDOWN_STORAGE_KEY);
  }
  return false;
};

export const getOtpErrorCode = (error: unknown) => {
  if (!(error instanceof Error)) return;
  const graphQLErrors = (
    error as Error & {
      graphQLErrors?: Array<{ extensions?: { code?: string } }>;
    }
  ).graphQLErrors;

  return graphQLErrors?.[0]?.extensions?.code;
};

export const getErrorMessage = (error: unknown) => {
  if (!(error instanceof Error)) return;
  const graphQLError = (
    error as Error & {
      graphQLErrors?: Array<{ message?: string }>;
    }
  ).graphQLErrors?.[0];

  return graphQLError?.message || error.message;
};
